import type { DebtDetail, Installment } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { BaseEstimate } from '../components/Money';
import { PageHeader } from '../components/PageHeader';
import { TransactionRow } from '../components/TransactionRow';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import { useDebt } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount, groupAmountInput } from '../lib/money';

/** One debt: what is outstanding (from the ledger), who it is with, its history, and Pay. */
export function DebtDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const me = useMe();
  const debt = useDebt(id);
  if (!me.data || debt.isPending) return <p role="status">{t('app.loading')}</p>;
  if (debt.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('debts.title')} back="/debts" />
        <Card role="alert" className="flex flex-col gap-3">
          <p>{t('errors.debt.not_found')}</p>
          <Button variant="secondary" onClick={() => void debt.refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      </div>
    );
  }
  const d = debt.data;
  const { baseCurrency } = me.data.family;
  const money = (amount: string) => formatAmount(amount, d.currency, i18n.language);
  const owesLine =
    d.direction === 'borrowed'
      ? t('debts.owes', { owner: d.owner.displayName, person: d.person.name })
      : t('debts.owesOwner', { owner: d.owner.displayName, person: d.person.name });
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.person.name} back="/debts" />
      <section className="flex flex-col gap-2 rounded-3xl bg-hero p-5 text-hero-fg">
        <span className="text-[13px] text-hero-muted">{owesLine}</span>
        <span
          dir="ltr"
          className="text-[38px] leading-tight font-semibold tracking-[-0.5px] tabular-nums"
        >
          {money(d.outstanding)}
        </span>
        <span className="text-[13px] text-hero-muted">
          {d.settled ? t('debts.settled') : t('debts.of', { amount: money(d.principal) })}
          {' · '}
          {t('debts.paidSoFar', { amount: money(d.paid) })}
        </span>
        {d.currency !== baseCurrency && (
          <span className="text-hero-muted">
            <BaseEstimate total={d.outstandingInBase} />
          </span>
        )}
      </section>
      {d.note && <p className="px-1 text-sm text-muted">{d.note}</p>}
      {d.credit && (
        <Link
          to={`/activity/${d.credit.transactionId}`}
          className={buttonVariants({ variant: 'secondary' })}
        >
          {[
            `${t('credit.item')}: ${d.credit.name || t('credit.item')}`,
            d.credit.dueOn && !d.settled
              ? t('credit.due', {
                  date: formatDay(d.credit.dueOn, todayIn(me.data.family.timezone), i18n.language, {
                    today: t('activity.today'),
                    yesterday: t('activity.yesterday'),
                  }),
                })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Link>
      )}
      {!d.settled && d.nextInstallment && (
        <Link
          to={`/debts/${d.id}/pay`}
          state={{
            installment: d.nextInstallment.number,
            prefill: {
              amount: groupAmountInput(d.nextInstallment.amount, d.currency),
              ...(d.prefill.interest && !d.internal && d.direction === 'borrowed'
                ? { interest: groupAmountInput(d.prefill.interest, d.currency) }
                : {}),
            },
          }}
          className={buttonVariants()}
        >
          {d.direction === 'borrowed'
            ? t('debts.payInstallment', { number: d.nextInstallment.number })
            : t('debts.collectInstallment', { number: d.nextInstallment.number })}
        </Link>
      )}
      {!d.settled && (
        <Link
          to={`/debts/${d.id}/pay`}
          className={buttonVariants(
            d.nextInstallment ? { variant: 'secondary', block: true } : undefined,
          )}
        >
          {d.direction === 'borrowed' ? t('debts.pay') : t('debts.collect')}
        </Link>
      )}
      <PlanSection debt={d} timezone={me.data.family.timezone} />
      <div className="flex flex-wrap gap-2">
        {d.person.type === 'outside' && (
          <Link
            to={`/people/${d.person.personId}`}
            className={buttonVariants({ variant: 'secondary' })}
          >
            {t('debts.people')}: {d.person.name}
          </Link>
        )}
        {d.pairDebtId && (
          <Link to={`/debts/${d.pairDebtId}`} className={buttonVariants({ variant: 'secondary' })}>
            {t('debts.seeOtherSide')}
          </Link>
        )}
      </div>
      <section className="flex flex-col gap-2">
        <SectionLabel>{t('debts.history')}</SectionLabel>
        <ListGroup>
          {d.history.map((tx) => (
            <li key={tx.id}>
              <TransactionRow tx={tx} baseCurrency={baseCurrency} />
            </li>
          ))}
        </ListGroup>
      </section>
    </div>
  );
}

/** The installment plan: its terms, every installment with its state, Skip and Change plan. */
function PlanSection({ debt, timezone }: { debt: DebtDetail; timezone: string }) {
  const { t, i18n } = useTranslation();
  const invalidate = useInvalidateLedger();
  const skip = useMutation({
    mutationFn: (number: number) => api.debts.skipInstallment(debt.id, number),
    onSuccess: () => invalidate(),
  });
  const plan = debt.plan;
  if (!plan) {
    return debt.canManagePlan && !debt.settled ? (
      <Link
        to={`/debts/${debt.id}/plan`}
        className={buttonVariants({ variant: 'secondary', block: true })}
      >
        {t('debts.setUpPlan')}
      </Link>
    ) : null;
  }
  const today = todayIn(timezone);
  const money = (amount: string) => formatAmount(amount, debt.currency, i18n.language);
  const day = (date: string) =>
    formatDay(date, today, i18n.language, {
      today: t('activity.today'),
      yesterday: t('activity.yesterday'),
    });
  const open = plan.installments.filter((i) => i.state !== 'paid' && i.state !== 'skipped');
  const confirmSkip = (i: Installment) => {
    if (window.confirm(t('debts.skipConfirm', { number: i.number, date: day(i.dueOn) }))) {
      skip.mutate(i.number);
    }
  };
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{t('debts.installments')}</SectionLabel>
      <p className="px-1 text-sm" dir="auto">
        {t('debts.planLine', { amount: money(plan.amount), day: plan.dayOfMonth })}
        {' · '}
        {plan.finished
          ? t('debts.planFinished')
          : plan.count === null
            ? t('debts.untilPaid')
            : t('debts.planCount', { count: open.length })}
      </p>
      <FormError error={skip.error} />
      <ListGroup>
        {plan.installments.map((i) => {
          const isOpen = i.state !== 'paid' && i.state !== 'skipped';
          return (
            <li key={i.number} className="flex min-h-14 items-center gap-3 px-4 py-2">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-[15px] font-medium">
                  {t('debts.installmentNumber', { number: i.number })}
                </span>
                <span className="text-xs text-muted">{day(i.dueOn)}</span>
              </span>
              <span className="flex shrink-0 flex-col items-end">
                <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
                  {money(i.amount)}
                </span>
                <span
                  className={
                    i.state === 'overdue'
                      ? 'text-xs font-semibold text-expense'
                      : 'text-xs text-muted'
                  }
                >
                  {t(`debts.states.${i.state}`)}
                </span>
              </span>
              {isOpen && debt.canManagePlan && (
                <Button
                  variant="ghost"
                  className="min-h-11 shrink-0 px-3 text-[13px]"
                  aria-label={t('debts.skipAria', { number: i.number })}
                  disabled={skip.isPending}
                  onClick={() => confirmSkip(i)}
                >
                  {t('debts.skip')}
                </Button>
              )}
            </li>
          );
        })}
      </ListGroup>
      {debt.canManagePlan && !debt.settled && (
        <Link
          to={`/debts/${debt.id}/plan`}
          className={buttonVariants({ variant: 'secondary', block: true })}
        >
          {t('debts.changePlan')}
        </Link>
      )}
    </section>
  );
}
